import React from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/function";

import { CallToAction } from "src/components/cta/cta_helpers";
import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";
import { PageTitle, SectionTitle } from "src/components/typography/typography";
import { Separator } from "src/components/separator/separator";
import { StepIndicator } from "src/components/step_indicator/step_indicator";
import { Viewer } from "src/generated/types";

import { ChangePasswordForm } from "./forms/change_password";
import { ManageUsersForm } from "./forms/manage_users";
import { SchoolInfoForm } from "./forms/school_info";
import "./account.scss";

/*
 * GraphQL
 */

const VIEWER_ACCOUNT_QUERY = gql`
    query ViewerAccountInfo {
        viewer {
            user {
                id
                school {
                    id
                    address
                    name
                    objectId
                    currentReport {
                        id
                        status
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloData {
    viewer: Viewer;
}

/*
 * Main
 */

export const AccountPage = (): React.ReactElement => {
    const schoolInfoRef = React.createRef<HTMLDivElement>();
    const changePasswordRef = React.createRef<HTMLDivElement>();
    const manageUsersRef = React.createRef<HTMLDivElement>();
    const { data, error, loading } = useQuery<ApolloData, Empty>(VIEWER_ACCOUNT_QUERY);

    if (loading) return <Loading />;
    if (error) return <Error />;

    return pipe(
        O.fromNullable(data?.viewer.user.school?.currentReport),
        O.fold(
            () => (
                <div className="main-page-wrap short">
                    <div className="center">
                        <div className="title-bar-with-extra">
                            <PageTitle>My Account</PageTitle>
                        </div>
                        <Separator />
                        <div className="page-section">
                            <SectionTitle>Change Your Password</SectionTitle>
                            <ChangePasswordForm userId={data?.viewer.user.id} />
                        </div>
                    </div>
                </div>
            ),
            (currentReport) => (
                <div className="main-page-wrap short">
                    <div className="center">
                        <StepIndicator report={currentReport} />
                        <div className="title-bar-with-extra">
                            <PageTitle>My Account</PageTitle>
                            <div className="title-bar-extra-wrap">
                                Jump to:&nbsp;&nbsp;&nbsp;
                                <button
                                    className="jump-to-link"
                                    onClick={(): void => {
                                        schoolInfoRef?.current?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    }}
                                >
                                    School Information
                                </button>
                                <span className="title-divider">|</span>
                                <button
                                    className="jump-to-link"
                                    onClick={(): void => {
                                        changePasswordRef?.current?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    }}
                                >
                                    Change Password
                                </button>
                                <span className="title-divider">|</span>
                                <button
                                    className="jump-to-link"
                                    onClick={(): void => {
                                        manageUsersRef?.current?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    }}
                                >
                                    Manage Users
                                </button>
                            </div>
                        </div>
                        <Separator />
                        <div className="page-section" ref={schoolInfoRef}>
                            <SectionTitle>School Information</SectionTitle>
                            <SchoolInfoForm school={data?.viewer.user.school} />
                        </div>
                        <Separator />
                        <div className="page-section" ref={changePasswordRef}>
                            <SectionTitle>Change Your Password</SectionTitle>
                            <ChangePasswordForm userId={data?.viewer.user.id} />
                        </div>
                        <Separator />
                        <div className="page-section" ref={manageUsersRef}>
                            <SectionTitle>Manage Users</SectionTitle>
                            <ManageUsersForm schoolId={data?.viewer.user.school?.objectId} />
                        </div>
                        <Separator />
                        <CallToAction currentReport={currentReport} schoolId={data?.viewer.user.school?.id} />
                    </div>
                </div>
            ),
        ),
    );
};
